/**
 * @file Scoped task query builders for the main "My Tasks" view.
 */

import {
    collection,
    limit,
    orderBy,
    query,
    startAfter,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../firebase/firebase.js";

const TASK_PARENT_TYPES = Object.freeze([
    { permission: "patients", parentType: "patient" },
    { permission: "projects", parentType: "project" },
    { permission: "assets", parentType: "asset" },
    { permission: "labs", parentType: "lab" },
    { permission: "maintenance", parentType: "maintenance" },
]);

/**
 * Returns parent task scopes the user can read from parent access.
 * @param {object | null | undefined} userProfile Current user profile.
 * @returns {Array<string>} Singular task parentType values.
 */
export function getReadableTaskParentTypes(userProfile) {
    if (userProfile?.role === "admin") {
        return TASK_PARENT_TYPES.map((entry) => entry.parentType);
    }

    const permissions = Array.isArray(userProfile?.permissions) ?
        userProfile.permissions :
        [];

    return TASK_PARENT_TYPES
        .filter((entry) => permissions.includes(entry.permission))
        .map((entry) => entry.parentType);
}

/**
 * Builds stable task access scopes for owner/assignee queries.
 * @param {object | null | undefined} userProfile Current user profile.
 * @returns {Array<object>} Query scope descriptors.
 */
export function getMyTaskAccessScopes(userProfile) {
    return [
        {
            key: "standalone",
            type: "standalone",
            constraints: [where("isSubtask", "==", false)],
        },
        ...getReadableTaskParentTypes(userProfile).map((parentType) => ({
            key: `parent-${parentType}`,
            type: "parent",
            parentType,
            constraints: [
                where("parentType", "==", parentType),
                where("isSubtask", "==", true),
            ],
        })),
    ];
}

/**
 * Returns a stable cursor key for a task query scope.
 * @param {object} params Key params.
 * @param {"owner"|"assigned"} params.assignment Assignment slice.
 * @param {object} params.scope Task access scope.
 * @returns {string} Cursor key.
 */
export function getTaskQueryScopeKey({ assignment, scope }) {
    return `${assignment}:${scope.key}`;
}

/**
 * Builds a query for one "My Tasks" assignment/access slice.
 * @param {object} params Query params.
 * @param {string} params.userId Current user id.
 * @param {"owner"|"assigned"} params.assignment Assignment slice.
 * @param {object} params.scope Task access scope from getMyTaskAccessScopes.
 * @param {boolean} params.isClosed Closed-state filter.
 * @param {number | null} [params.limitCount] Optional limit.
 * @param {object | null} [params.afterDoc] Optional pagination cursor.
 * @returns {object} Query descriptor.
 */
export function buildMyTaskQuery({
    userId,
    assignment,
    scope,
    isClosed,
    limitCount = null,
    afterDoc = null,
}) {
    const assignmentConstraint = assignment === "assigned" ?
        where("assigned_users", "array-contains", userId) :
        where("owner", "==", userId);

    const constraints = [
        assignmentConstraint,
        ...scope.constraints,
        where("isClosedResolved", "==", isClosed),
        orderBy("updatedAt", "desc"),
    ];

    if (afterDoc) {
        constraints.push(startAfter(afterDoc));
    }
    if (limitCount) {
        constraints.push(limit(limitCount));
    }

    return {
        key: getTaskQueryScopeKey({ assignment, scope }),
        assignment,
        scopeKey: scope.key,
        scopeType: scope.type,
        parentType: scope.parentType || null,
        queryRef: query(collection(db, "tasks"), ...constraints),
    };
}

/**
 * Builds all scoped "My Tasks" query descriptors for a user.
 * @param {object} params Query params.
 * @param {string} params.userId Current user id.
 * @param {object | null | undefined} params.userProfile Current user profile.
 * @param {boolean} params.isClosed Closed-state filter.
 * @param {number | null} [params.limitCount] Optional query limit.
 * @returns {Array<object>} Query descriptors.
 */
export function buildMyTaskQueries({
    userId,
    userProfile,
    isClosed,
    limitCount = null,
}) {
    const scopes = getMyTaskAccessScopes(userProfile);
    return ["owner", "assigned"].flatMap((assignment) =>
        scopes.map((scope) => buildMyTaskQuery({
            userId,
            assignment,
            scope,
            isClosed,
            limitCount,
        })),
    );
}

/**
 * Rebuilds a query from stored pagination scope state.
 * @param {object} params Query params.
 * @param {string} params.userId Current user id.
 * @param {object} params.scopeState Stored pagination scope state.
 * @param {number} params.limitCount Query limit.
 * @returns {object | null} Query descriptor or null when state is incomplete.
 */
export function buildMyTaskPaginationQuery({
    userId,
    scopeState,
    limitCount,
}) {
    if (!scopeState?.lastDoc || !scopeState?.hasMore) {return null;}

    const scope = scopeState.scopeType === "parent" ?
        {
            key: scopeState.scopeKey,
            type: "parent",
            parentType: scopeState.parentType,
            constraints: [
                where("parentType", "==", scopeState.parentType),
                where("isSubtask", "==", true),
            ],
        } :
        {
            key: "standalone",
            type: "standalone",
            constraints: [where("isSubtask", "==", false)],
        };

    return buildMyTaskQuery({
        userId,
        assignment: scopeState.assignment,
        scope,
        isClosed: true,
        limitCount,
        afterDoc: scopeState.lastDoc,
    });
}
